import { useState, useEffect, useCallback } from 'react'
import {
  Search, FileText, Plus, Trash2, ChevronDown, ChevronRight, X, Loader2, Calendar,
  Edit3, Check, RefreshCw, Globe, Building2, HelpCircle,
} from 'lucide-react'
import { dealsApi, transcriptsApi, hubspotExcerptsApi, type DealTranscript, type HubspotExcerpt, type HubspotExcerptSource } from '@api/client'
import type { Deal } from '@api/types'
import { showToast } from '@store/uiStore'

export function TranscriptsTool() {
  const [showHelp, setShowHelp] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<Deal[]>([])
  const [searching, setSearching] = useState(false)
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null)

  // Debounced deal search
  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return }
    const timer = setTimeout(async () => {
      setSearching(true)
      const res = await dealsApi.list({ search: searchQuery.trim(), pageSize: 10 })
      if (res.success && res.data) setSearchResults(res.data.items)
      setSearching(false)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const selectDeal = (deal: Deal) => {
    setSelectedDeal(deal)
    setSearchQuery('')
    setSearchResults([])
  }

  const deleteDeal = async (deal: Deal) => {
    if (!confirm(`Delete deal "${deal.customer_name}"? This cannot be undone.`)) return
    const res = await dealsApi.delete(deal.id)
    if (res.success) {
      showToast('Deal deleted', 'success')
      if (selectedDeal?.id === deal.id) setSelectedDeal(null)
    } else {
      showToast(res.error || 'Failed to delete deal', 'error')
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Transcripts & HubSpot Excerpts</h2>
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="p-1 text-[var(--text-muted)] hover:text-blue-400 transition-colors"
            title="How does this work?"
          >
            <HelpCircle className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          Centralised view of call transcripts and AI-condensed HubSpot activity for any deal. These feed into Blueprint AI, Demo Prep, and other tools.
        </p>

        {showHelp && (
          <div className="mt-3 rounded-lg border border-blue-500/25 bg-blue-500/5 p-4 text-xs text-[var(--text-secondary)] space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-[var(--text-primary)]">How does this work?</span>
              <button onClick={() => setShowHelp(false)} className="p-0.5 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            <div>
              <p className="font-semibold text-[var(--text-primary)] mb-1">HubSpot Excerpts</p>
              <p>When you click <strong>Generate</strong>, the system pulls all activity from the linked HubSpot deal &mdash; deal properties (MEDDPICC), notes, emails, calls, meetings, and tasks (up to 20 of each).</p>
              <ul className="list-disc list-inside mt-1.5 space-y-1">
                <li><strong>AI note-taker content</strong> (Avoma, Google Gemini, Fireflies, Otter) is detected automatically and <em>preserved verbatim</em> &mdash; these are already summarised and too valuable to re-condense.</li>
                <li><strong>Everything else</strong> (emails, manual notes, call logs, tasks) is condensed by AI into structured sections: stakeholders, pain points, current architecture, country details, requirements, timeline, and competitors.</li>
              </ul>
              <p className="mt-1.5">The source badges show you exactly what was captured. If you don&apos;t see Avoma/Gemini badges, the call notes haven&apos;t made it into HubSpot yet and you may want to upload them manually as transcripts below.</p>
              <p className="mt-1.5">The excerpt is stored and reused by Blueprint AI, Scope Suggest, and Demo Prep &mdash; generate once, refresh when new activity comes in.</p>
            </div>

            <div>
              <p className="font-semibold text-[var(--text-primary)] mb-1">Transcripts</p>
              <p>Transcripts are manual &mdash; paste in call recordings, Avoma summaries, or meeting notes. They&apos;re stored against the deal and also feed into AI tools like Blueprint and Scope Suggest when you use the &ldquo;Include transcripts&rdquo; option.</p>
            </div>
          </div>
        )}
      </div>

      {/* Deal selector */}
      <div className="relative">
        <div className="flex items-center gap-2">
          {selectedDeal ? (
            <div className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)]">
              <Building2 className="w-4 h-4 text-[var(--text-muted)]" />
              <span className="text-sm font-medium text-[var(--text-primary)]">{selectedDeal.customer_name}</span>
              {selectedDeal.hubspot_deal_name && (
                <span className="text-xs text-[var(--text-muted)]">({selectedDeal.hubspot_deal_name})</span>
              )}
              <div className="ml-auto flex items-center gap-1">
                {!selectedDeal.hubspot_deal_id && (
                  <button
                    onClick={() => deleteDeal(selectedDeal)}
                    className="p-1 text-[var(--text-muted)] hover:text-red-400 transition-colors"
                    title="Delete this unlinked deal"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  onClick={() => setSelectedDeal(null)}
                  className="p-0.5 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
              <input
                type="text"
                placeholder="Search for a deal by customer name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
                autoFocus
              />
              {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[var(--text-muted)]" />}
            </div>
          )}
        </div>

        {/* Search results dropdown */}
        {searchResults.length > 0 && !selectedDeal && (
          <div className="absolute z-10 mt-1 w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-lg max-h-60 overflow-y-auto">
            {searchResults.map((deal) => (
              <button
                key={deal.id}
                onClick={() => selectDeal(deal)}
                className="w-full px-4 py-2.5 text-left hover:bg-[var(--bg-tertiary)] transition-colors flex items-center gap-3"
              >
                <Building2 className="w-4 h-4 text-[var(--text-muted)] shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[var(--text-primary)] truncate">{deal.customer_name}</p>
                  {deal.hubspot_deal_name && (
                    <p className="text-xs text-[var(--text-muted)] truncate">{deal.hubspot_deal_name}</p>
                  )}
                </div>
                <span className={`ml-auto px-2 py-0.5 text-[10px] font-medium rounded shrink-0 ${
                  deal.status === 'finalized' ? 'bg-emerald-500/20 text-emerald-400'
                  : deal.status === 'in_review' ? 'bg-amber-500/20 text-amber-400'
                  : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)]'
                }`}>{deal.status}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Deal content */}
      {selectedDeal && (
        <div className="space-y-5">
          <HubSpotExcerptSection dealId={selectedDeal.id} hasHubspot={!!selectedDeal.hubspot_deal_id} />
          <TranscriptsSection dealId={selectedDeal.id} />
        </div>
      )}

      {!selectedDeal && (
        <div className="text-center py-12">
          <FileText className="w-10 h-10 text-[var(--text-muted)] mx-auto mb-3 opacity-50" />
          <p className="text-sm text-[var(--text-muted)]">Select a deal to view its transcripts and HubSpot excerpt.</p>
        </div>
      )}
    </div>
  )
}

// ---- Source Badges ----

const SOURCE_BADGE_CONFIG: Record<string, { label: string; color: string }> = {
  avoma: { label: 'Avoma', color: 'bg-violet-500/20 text-violet-400' },
  gemini: { label: 'Gemini', color: 'bg-blue-500/20 text-blue-400' },
  fireflies: { label: 'Fireflies', color: 'bg-orange-500/20 text-orange-400' },
  otter: { label: 'Otter', color: 'bg-cyan-500/20 text-cyan-400' },
}

function SourceBadges({ sourcesJson }: { sourcesJson: string | null }) {
  if (!sourcesJson) return null
  let sources: HubspotExcerptSource[] = []
  try { sources = JSON.parse(sourcesJson) } catch { return null }

  const aiSources = sources.filter((s) => s.type in SOURCE_BADGE_CONFIG)
  if (!aiSources.length) return null

  return (
    <>
      {aiSources.map((s) => {
        const cfg = SOURCE_BADGE_CONFIG[s.type]!
        return (
          <span key={s.type} className={`px-1.5 py-0.5 text-[9px] font-medium rounded ${cfg.color}`}>
            {s.count}x {cfg.label}
          </span>
        )
      })}
    </>
  )
}

// ---- HubSpot Excerpt Section ----

function HubSpotExcerptSection({ dealId, hasHubspot }: { dealId: number; hasHubspot: boolean }) {
  const [excerpt, setExcerpt] = useState<HubspotExcerpt | null>(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const fetchExcerpt = useCallback(async () => {
    setLoading(true)
    const res = await hubspotExcerptsApi.get(dealId)
    if (res.success) setExcerpt(res.data ?? null)
    setLoading(false)
  }, [dealId])

  useEffect(() => { fetchExcerpt() }, [fetchExcerpt])

  const handleGenerate = async () => {
    setGenerating(true)
    try {
      const res = await hubspotExcerptsApi.generate(dealId)
      if (res.success && res.data) {
        setExcerpt(res.data)
        setExpanded(true)
        showToast('HubSpot excerpt generated', 'success')
      } else {
        showToast(res.error || 'Failed to generate excerpt', 'error')
      }
    } catch {
      showToast('Network error', 'error')
    }
    setGenerating(false)
  }

  if (loading) {
    return (
      <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] p-4">
        <div className="flex items-center gap-2 text-[var(--text-muted)]">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-xs">Loading HubSpot excerpt...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-[var(--bg-secondary)]">
        <button
          onClick={() => excerpt && setExpanded(!expanded)}
          className="flex items-center gap-2 text-left"
          disabled={!excerpt}
        >
          {excerpt ? (
            expanded ? <ChevronDown className="w-4 h-4 text-[var(--text-muted)]" /> : <ChevronRight className="w-4 h-4 text-[var(--text-muted)]" />
          ) : (
            <Globe className="w-4 h-4 text-[var(--text-muted)]" />
          )}
          <span className="text-sm font-semibold text-[var(--text-primary)]">HubSpot Excerpt</span>
          {excerpt && (
            <>
              <span className="px-1.5 py-0.5 text-[9px] font-medium bg-[var(--bg-tertiary)] text-[var(--text-muted)] rounded">
                {excerpt.activity_count} activities
              </span>
              <SourceBadges sourcesJson={excerpt.sources_json} />
              <span className="text-[10px] text-[var(--text-muted)]">
                Generated {new Date(excerpt.generated_at).toLocaleDateString()}
              </span>
            </>
          )}
        </button>
        <div className="flex items-center gap-2">
          {!hasHubspot && (
            <span className="text-[10px] text-amber-400">No HubSpot deal linked</span>
          )}
          <button
            onClick={handleGenerate}
            disabled={generating || !hasHubspot}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-400 bg-blue-500/10 border border-blue-500/25 rounded-lg hover:bg-blue-500/20 transition-colors disabled:opacity-50"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {excerpt ? 'Refresh' : 'Generate'}
          </button>
        </div>
      </div>

      {generating && (
        <div className="px-4 py-6 flex items-center justify-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
          <span className="text-sm text-[var(--text-secondary)]">Fetching HubSpot activities and condensing with AI...</span>
        </div>
      )}

      {expanded && excerpt && !generating && (
        <div className="px-4 py-4 border-t border-[var(--border-color)]">
          <pre className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap font-sans leading-relaxed">{excerpt.excerpt}</pre>
        </div>
      )}

      {!excerpt && !generating && (
        <div className="px-4 py-4 text-center">
          <p className="text-xs text-[var(--text-muted)]">
            {hasHubspot
              ? 'No excerpt yet. Click "Generate" to fetch and condense all HubSpot activity (notes, emails, calls, meetings, tasks) into a reusable brief.'
              : 'Link this deal to a HubSpot deal first to generate an excerpt.'}
          </p>
        </div>
      )}
    </div>
  )
}

// ---- Transcripts Section ----

function TranscriptsSection({ dealId }: { dealId: number }) {
  const [transcripts, setTranscripts] = useState<DealTranscript[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [adding, setAdding] = useState(false)
  const [saving, setSaving] = useState(false)

  // New transcript form
  const [newTitle, setNewTitle] = useState('')
  const [newDate, setNewDate] = useState(new Date().toISOString().split('T')[0])
  const [newText, setNewText] = useState('')

  // Edit state
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editDate, setEditDate] = useState('')
  const [editText, setEditText] = useState('')

  useEffect(() => {
    setLoading(true)
    setExpandedId(null)
    setAdding(false)
    setEditingId(null)
    transcriptsApi.list(dealId).then((res) => {
      if (res.success && res.data) setTranscripts(res.data)
      else setTranscripts([])
    }).finally(() => setLoading(false))
  }, [dealId])

  const handleAdd = async () => {
    if (!newText.trim() || !newDate) return
    setSaving(true)
    const res = await transcriptsApi.create(dealId, {
      title: newTitle.trim() || `Call on ${newDate}`,
      callDate: newDate,
      transcript: newText.trim(),
    })
    if (res.success && res.data) {
      setTranscripts((prev) => [res.data!, ...prev])
      setAdding(false)
      setNewTitle('')
      setNewDate(new Date().toISOString().split('T')[0])
      setNewText('')
      showToast('Transcript saved', 'success')
    }
    setSaving(false)
  }

  const handleUpdate = async (id: number) => {
    setSaving(true)
    const res = await transcriptsApi.update(dealId, id, {
      title: editTitle.trim(),
      callDate: editDate,
      transcript: editText.trim(),
    })
    if (res.success && res.data) {
      setTranscripts((prev) => prev.map((t) => (t.id === id ? res.data! : t)))
      setEditingId(null)
      showToast('Transcript updated', 'success')
    }
    setSaving(false)
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this transcript? This cannot be undone.')) return
    const res = await transcriptsApi.remove(dealId, id)
    if (res.success) {
      setTranscripts((prev) => prev.filter((t) => t.id !== id))
      if (expandedId === id) setExpandedId(null)
      showToast('Transcript deleted', 'success')
    }
  }

  const startEdit = (t: DealTranscript) => {
    setEditingId(t.id)
    setEditTitle(t.title)
    setEditDate(t.call_date)
    setEditText(t.transcript)
    setExpandedId(t.id)
  }

  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-[var(--bg-secondary)]">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-semibold text-[var(--text-primary)]">Call Transcripts</span>
          <span className="px-1.5 py-0.5 text-[9px] font-medium bg-[var(--bg-tertiary)] text-[var(--text-muted)] rounded">
            {transcripts.length} saved
          </span>
        </div>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-400 bg-blue-500/10 border border-blue-500/25 rounded-lg hover:bg-blue-500/20 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add transcript
          </button>
        )}
      </div>

      <div className="p-4 space-y-3">
        {/* Add form */}
        {adding && (
          <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-4 space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder="Title (e.g. Discovery call with John)"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                className="flex-1 px-3 py-2 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
              />
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-[var(--text-muted)]" />
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  className="px-2 py-2 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)]"
                />
              </div>
            </div>
            <textarea
              placeholder="Paste the transcript, call notes, or meeting summary here..."
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              className="w-full px-3 py-2 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] resize-y font-mono"
              rows={10}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setAdding(false); setNewTitle(''); setNewText('') }}
                className="px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={saving || !newText.trim() || !newDate}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Save transcript
              </button>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-[var(--text-muted)]" />
          </div>
        )}

        {/* Empty state */}
        {!loading && transcripts.length === 0 && !adding && (
          <div className="text-center py-8">
            <FileText className="w-8 h-8 text-[var(--text-muted)] mx-auto mb-2 opacity-50" />
            <p className="text-sm text-[var(--text-muted)]">No transcripts yet</p>
            <p className="text-xs text-[var(--text-muted)] mt-1">Add call transcripts, Avoma summaries, or meeting notes so they can be reused across the deal.</p>
          </div>
        )}

        {/* Transcript list */}
        {!loading && transcripts.map((t) => {
          const isExpanded = expandedId === t.id
          const isEditing = editingId === t.id
          const wordCount = t.transcript.split(/\s+/).filter(Boolean).length
          const preview = t.transcript.slice(0, 150).replace(/\n/g, ' ')

          return (
            <div key={t.id} className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
              <button
                onClick={() => setExpandedId(isExpanded ? null : t.id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--bg-tertiary)] transition-colors"
              >
                {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-[var(--text-muted)] shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-[var(--text-muted)] shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[var(--text-primary)] truncate">{t.title}</span>
                    <span className="text-[10px] text-[var(--text-muted)] shrink-0">{t.call_date}</span>
                    <span className="text-[10px] text-[var(--text-muted)] shrink-0">{wordCount.toLocaleString()} words</span>
                  </div>
                  {!isExpanded && (
                    <p className="text-[11px] text-[var(--text-muted)] truncate mt-0.5">{preview}...</p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => isEditing ? setEditingId(null) : startEdit(t)}
                    className="p-1.5 text-[var(--text-muted)] hover:text-blue-400 transition-colors"
                    title="Edit"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(t.id)}
                    className="p-1.5 text-[var(--text-muted)] hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 border-t border-[var(--border-color)]">
                  {isEditing ? (
                    <div className="space-y-3 pt-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="text"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          className="flex-1 px-3 py-2 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)]"
                        />
                        <input
                          type="date"
                          value={editDate}
                          onChange={(e) => setEditDate(e.target.value)}
                          className="px-2 py-2 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)]"
                        />
                      </div>
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full px-3 py-2 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] resize-y font-mono"
                        rows={12}
                      />
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-xs text-[var(--text-secondary)]">
                          Cancel
                        </button>
                        <button
                          onClick={() => handleUpdate(t.id)}
                          disabled={saving}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg disabled:opacity-50"
                        >
                          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          Save changes
                        </button>
                      </div>
                    </div>
                  ) : (
                    <pre className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap font-mono pt-3 max-h-80 overflow-y-auto">{t.transcript}</pre>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
